import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import {
    useMediaQuery,
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogContent,
    DialogTitle
} from '@material-ui/core';
import {SectionHeader} from 'components/molecules';

import {isEmail} from "common";
import {crmCreateContact} from "api/crmApi";

const useStyles = makeStyles(theme => ({
    form: {
        maxWidth: 550,
        margin: `0 auto`,
        '& .MuiTextField-root': {
            background: theme.palette.background.paper,
        },
        '& .MuiOutlinedInput-input': {
            background: theme.palette.background.paper,
        },
    },
    inputTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(1),
    },
    title: {
        background: theme.palette.secondary.main
    },
    fontWeight900: {
        fontWeight: 900,
        color: 'white'
    },
    error: {
        color: theme.palette.error.main
    },
    success: {
        fontWeight: 900
    }
}));

const ContactForm = props => {
    const {className, onClose, ...rest} = props;
    const classes = useStyles();
    
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });
    
    const [email, setEmail] = React.useState("");
    const [name, setName] = React.useState("");
    const [message, setMessage] = React.useState("")
    const [status, setStatus] = React.useState();
    
    const onEnd = () => {
        setEmail("")
        setName("")
        setMessage("")
        setStatus(null);
        onClose();
    }
    
    const onSubmit = async () => {
        setStatus(null);
        
        try {
            const response =
               await crmCreateContact(email, name, message);
            if (!response.ok) {
                setStatus("failed");
            }
            
            setStatus("success")
        } catch (e) {
            console.error(e);
            setStatus("failed");
        } finally {
            setTimeout(onEnd, (status === "failed") ? 30000 : 5000);
        }
    }
    
    const disabled = !email || !name || !message || !isEmail(email);
    
    const emailHelp = email && email.length && !isEmail(email);
    const title = !status ? "Can't find the answer you need?" :
       status === "success" ? `Hey ${name}, we have your message and we will be in touch very shortly!` :
          <>Send us an email directly at <a href="mailto:sales@rev-lock.com">sales@rev-lock.com</a></>
    const subtitle = !status ? "We are always up to talk accounting, revenue or whatever is on your mind." : "Thanks again for your interest"
    
    return (
       <div className={className} {...rest}>
           <SectionHeader
              title={title}
              subtitle={subtitle}
              subtitleProps={{
                  variant: 'body1',
                  color: 'textPrimary',
              }}
              data-aos="fade-up"
              align={isMd ? 'center' : 'left'}
           />
           <div className={classes.form}>
               <Grid container spacing={isMd ? 4 : 2}>
                   {!status &&
                   <>
                       <Grid item xs={12} data-aos="fade-up">
                           <Typography
                              variant="subtitle1"
                              color="textPrimary"
                              className={classes.inputTitle}>
                               Full name
                           </Typography>
                           <TextField
                              placeholder="Your full name"
                              variant="outlined"
                              size="medium"
                              name="fullname"
                              fullWidth
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                           />
                       </Grid>
                       <Grid item xs={12} data-aos="fade-up">
                           <Typography
                              variant="subtitle1"
                              color="textPrimary"
                              className={classes.inputTitle}
                           >
                               E-mail
                           </Typography>
                           <TextField
                              placeholder="Your e-mail address"
                              variant="outlined"
                              size="medium"
                              name="email"
                              fullWidth
                              type="email"
                              helperText={emailHelp ? "Looks like this email is invalid" : null}
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                           />
                       </Grid>
                       <Grid item xs={12} data-aos="fade-up">
                           <Typography
                              variant="subtitle1"
                              color="textPrimary"
                              className={classes.inputTitle}
                           >
                               Message
                           </Typography>
                           <TextField
                              placeholder="Your question about our services"
                              variant="outlined"
                              name="message"
                              fullWidth
                              multiline
                              rows={4}
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                           />
                       </Grid>
                   </>
                   }
                   <Grid item container justify="center" xs={12}>
                       <Button
                          variant="contained"
                          type="submit"
                          color="primary"
                          size="large"
                          onClick={(e) => status ? onEnd(e) : onSubmit(e)}
                          disabled={disabled}>
                           {status ? 'done' : 'submit'}
                       </Button>
                   </Grid>
               </Grid>
           </div>
       </div>
    );
};

ContactForm.propTypes = {
    /**
     * External classes
     */
    className: PropTypes.string,
};


function ContactDialog(props) {
    const {open, onClose} = props;
    const classes = useStyles();
    
    return (<Dialog open={open} onClose={onClose}>
        <DialogTitle className={classes.title}>
            <Typography variant="h4" component="span" className={classes.fontWeight900}>
                Let's talk
            </Typography>
        </DialogTitle>
        <DialogContent>
            <ContactForm {...props}/>
        </DialogContent>
    </Dialog>)
}


export default ContactDialog;
